<div class="max-w-5xl mx-auto p-5 flex flex-col space-y-4">
  <!-- Greeting + login instructions card -->
  <section
    class="bg-slate-900 border border-slate-800 rounded-2xl p-5 md:p-6 shadow-lg flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
  >
    <!-- Left: greeting -->
    <div class="space-y-1">
      <h1 class="text-xl md:text-2xl font-semibold text-slate-50">
        {{ 'WelcomeBack' | localizeText : 'Welcome Back' }},
      </h1>
      <h1 class="text-xl md:text-2xl font-bold capitalize text-emerald-300">
        {{ auth.user$().userProfile?.firstName }}
      </h1>

      <p class="mt-2 text-xs md:text-sm text-slate-300 max-w-md">
        {{
          'MyOnboarding.Instructions'
            | localizeText
            : 'Continue your onboarding setup by signing into the iOS or Android application, using your login below'
        }}:
      </p>
    </div>

    <!-- Right: username / login pill -->
    <div
      class="mt-3 md:mt-0 w-full md:w-auto bg-slate-800/70 border border-slate-700 rounded-xl px-4 py-3 space-y-2"
    >
      <p
        class="text-[11px] font-semibold uppercase tracking-wide text-slate-400"
      >
        {{
          'MyOnboarding.LoginLabel'
            | localizeText
            : 'Your app login'
        }}
      </p>

      <div class="flex items-center gap-2">
        <span
          class="inline-flex items-center rounded-md bg-slate-900/80 px-3 py-1.5 font-mono text-xs md:text-sm text-emerald-200 border border-slate-700"
        >
          {{ username$() }}
        </span>
      </div>

      <p class="text-[11px] text-slate-500">
        {{
          'MyOnboarding.LoginHint'
            | localizeText
            : 'Use this username when signing into the FitMed iOS or Android app.'
        }}
      </p>
    </div>
  </section>

  <div class="w-full mx-auto space-y-6">
    @if (vm) {

      <!-- Overall header + progress bar -->
      <section class="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-lg">
        <div
          class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          <div>
            <h2 class="text-lg font-semibold text-slate-50">
              {{ 'MyOnboarding.Title' | localizeText : 'Onboarding progress' }}
            </h2>
            <p class="mt-1 text-sm text-slate-400">
              {{
                'MyOnboarding.OverallProgress'
                  | localizeText
                  : ("You're " + vm.overallPercent + '% of the way through your setup.')
              }}
            </p>
          </div>

          <div class="flex items-end gap-3">
            <div class="text-right">
              <p class="text-3xl font-bold text-emerald-400">
                {{ vm.overallPercent }}%
              </p>
              <p class="text-xs uppercase tracking-wide text-slate-500">
                {{ 'MyOnboarding.CompleteLabel' | localizeText : 'Complete' }}
              </p>
            </div>
          </div>
        </div>

        <div class="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-800">
          <div
            class="h-2 rounded-full bg-emerald-500 transition-all duration-300"
            [style.width.%]="vm.overallPercent"
          ></div>
        </div>
      </section>

      <!-- Phase cards -->
      @if (vm.phases.length === 0) {
        <div class="flex justify-center py-10 text-sm text-slate-500">
          {{
            'MyOnboarding.NoSteps'
              | localizeText
              : 'No onboarding steps found.'
          }}
        </div>
      } @else {
        <section class="grid gap-4 md:grid-cols-2">
          @for (phase of vm.phases; track phase.key) {
            <article
              class="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-4"
            >
              <!-- Phase header -->
              <header class="flex items-center justify-between gap-2">
                <div>
                  <h3
                    class="text-xs font-semibold uppercase tracking-wide text-slate-300"
                  >
                    {{ phase.label }}
                  </h3>
                  <p class="mt-0.5 text-xs text-slate-500">
                    {{
                      'MyOnboarding.StepsCompleted'
                        | localizeText
                        : (phase.completedCount + ' / ' + phase.totalCount + ' steps completed')
                    }}
                  </p>
                </div>

                <div class="flex items-center gap-2">
                  <span class="text-xs font-medium text-slate-400">
                    {{ phasePercent(phase) }}%
                  </span>
                  <div
                    class="h-2 w-20 overflow-hidden rounded-full bg-slate-800"
                  >
                    <div
                      class="h-2 bg-emerald-500 transition-all duration-300"
                      [style.width.%]="phasePercent(phase)"
                    ></div>
                  </div>
                </div>
              </header>

              <!-- Step list -->
              <ol class="space-y-3">
                @for (step of phase.steps; track step.id) {
                  <li class="flex items-start gap-3">
                    <!-- status dot -->
                    <div class="relative mt-1">
                      <div
                        class="h-3 w-3 rounded-full border"
                        [ngClass]="{
                          'bg-emerald-500 border-emerald-400':
                            step.status === 'completed',
                          'bg-amber-500 border-amber-400':
                            step.isCurrent && step.status !== 'completed',
                          'bg-slate-700 border-slate-600':
                            !step.isCurrent && step.status === 'pending',
                          'bg-slate-800 border-slate-700':
                            step.status === 'not_started'
                        }"
                      ></div>
                    </div>

                    <div class="flex-1">
                      <div class="flex items-center gap-2">
                        <p class="text-sm font-medium text-slate-50">
                          {{ step.title }}
                        </p>

                        @if (step.isCurrent) {
                          <span
                            class="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300"
                          >
                            {{
                              'MyOnboarding.CurrentStep'
                                | localizeText
                                : 'Current step'
                            }}
                          </span>
                        }
                      </div>

                      @if (step.description) {
                        <p class="mt-0.5 text-xs text-slate-400">
                          {{ step.description }}
                        </p>
                      }

                      @if (step.requiredFields?.length) {
                        <p class="mt-1 text-[11px] text-slate-500">
                          {{
                            'MyOnboarding.RequiredLabel'
                              | localizeText
                              : 'Required:'
                          }}
                          <span class="font-medium text-slate-300">
                            {{ step.requiredFields.join(', ') }}
                          </span>
                        </p>
                      }

                      <p
                        class="mt-1 text-[11px]"
                        [ngClass]="{
                          'text-emerald-400': step.status === 'completed',
                          'text-amber-300':
                            step.isCurrent && step.status !== 'completed',
                          'text-slate-500': step.status === 'not_started'
                        }"
                      >
                        @switch (step.status) {
                          @case ('completed') {
                            {{
                              'MyOnboarding.StatusCompleted'
                                | localizeText
                                : 'Completed'
                            }}
                          }
                          @case ('pending') {
                            {{
                              'MyOnboarding.StatusPending'
                                | localizeText
                                : 'In progress'
                            }}
                          }
                          @case ('not_started') {
                            {{
                              'MyOnboarding.StatusNotStarted'
                                | localizeText
                                : 'Not started'
                            }}
                          }
                        }
                      </p>
                    </div>
                  </li>
                }
              </ol>
            </article>
          }
        </section>
      }
    } @else {
      <div class="flex justify-center py-12 text-sm text-slate-500">
        {{
          'MyOnboarding.Loading'
            | localizeText
            : 'Loading onboarding…'
        }}
      </div>
    }
  </div>
</div>
